import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useI18n } from '../../contexts/I18nContext';
import { useWorkloadReport } from '../../hooks/useTaskReports';
import type { ReportFilters } from '../../lib/taskReportsApi';
import { FIRMIO_COLORS } from '../dashboard/charts';
import { formatHours, formatPercent, ReportCard, SectionState } from './common';
import { CHART_TOOLTIP_STYLE } from '../../lib/chartTheme';

// Open workload (remaining estimated hours) vs. weekly capacity per person.
// People over capacity get a red bar. Ignores the period filter by design.

export function WorkloadSection({ filters }: { filters: ReportFilters }) {
  const { t } = useI18n();
  const { data, isLoading, isError } = useWorkloadReport(filters);

  const people = data?.people ?? [];
  const chartData = people.map((p) => ({
    name: p.name,
    remaining: p.remainingHours,
    capacity: p.capacity,
    over: p.capacity > 0 && p.remainingHours > p.capacity,
  }));

  return (
    <ReportCard title={t('reports.workload.title')}>
      <SectionState isLoading={isLoading} isError={isError} />
      {data && people.length === 0 && (
        <p className="text-sm text-gray-400">{t('reports.workload.empty')}</p>
      )}
      {data && people.length > 0 && (
        <div className="space-y-4">
          <div style={{ height: Math.max(160, people.length * 36 + 48) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={140}
                  tick={{ fontSize: 12, fill: '#4b5563' }}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  contentStyle={CHART_TOOLTIP_STYLE}
                  formatter={(value: number) => formatHours(value)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar
                  dataKey="remaining"
                  name={t('reports.workload.remaining')}
                  radius={[0, 3, 3, 0]}
                >
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.name}
                      fill={entry.over ? FIRMIO_COLORS.red : FIRMIO_COLORS.blue}
                    />
                  ))}
                </Bar>
                <Bar
                  dataKey="capacity"
                  name={t('reports.workload.capacity')}
                  fill="#d1d5db"
                  radius={[0, 3, 3, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs">
                  <th className="px-3 py-2 text-left font-medium text-gray-500">
                    {t('reports.hours.person')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.workload.openTasks')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.workload.estimated')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.workload.remaining')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.workload.capacity')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">
                    {t('reports.workload.utilization')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => {
                  const over = p.utilization != null && p.utilization > 1;
                  return (
                    <tr key={p.userId} className="border-b border-gray-100">
                      <td className="px-3 py-2 text-gray-900">{p.name}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {p.openTasks}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {formatHours(p.openEstimatedHours)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-900">
                        {formatHours(p.remainingHours)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                        {formatHours(p.capacity)}
                      </td>
                      <td
                        className={`px-3 py-2 text-right tabular-nums font-medium ${
                          over ? 'text-red-600' : 'text-gray-600'
                        }`}
                      >
                        {p.utilization != null ? formatPercent(p.utilization) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </ReportCard>
  );
}
